'use strict'

const MarkdownIt = require('markdown-it')
const backticks = require('markdown-it/lib/rules_inline/backticks')
const { literalRanges, scanCritic, projectComments } = require('./critic-markup')

const unsafeCritic = /\{\+\+|\+\+\}|\{--|--\}|\{~~|~~\}|~>|\{==|==\}|\{>>|<<\}/
const unsafeMessage = 'Select text within one complete code span, without existing CriticMarkup delimiters.'
const md = new MarkdownIt({ html: true })

md.inline.ruler.at('backticks', function suggestionBackticks (state, silent) {
  const from = state.pos
  const count = state.tokens.length
  const matched = backticks(state, silent)
  if (matched && !silent) {
    const token = state.tokens[state.tokens.length - 1]
    const range = { from, to: state.pos }
    if (state.tokens.length > count && token.type === 'code_inline') {
      token.meta = range
    } else {
      const pending = state.env.suggestionUnmatched || (state.env.suggestionUnmatched = [])
      pending.push({ content: state.src, from, to: state.pos })
    }
  }
  return matched
})

function lineOffsets (source) {
  const offsets = [0]
  for (let i = 0; i < source.length; i++) {
    if (source[i] === '\n') offsets.push(i + 1)
  }
  return offsets
}

// Inline tokens omit list/quote prefixes. Only map a line when its source is
// unambiguous; a span crossing stripped prefixes must not rewrite containers.
function inlineOffsets (source, offsets, token) {
  const mapped = []
  const lines = token.content.split('\n')
  if (lines.length > token.map[1] - token.map[0]) return null
  for (let i = 0; i < lines.length; i++) {
    const line = token.map[0] + i
    const start = offsets[line]
    const raw = source.slice(start, offsets[line + 1] === undefined ? source.length : offsets[line + 1] - 1)
    const at = raw.indexOf(lines[i])
    if (at < 0 || raw.indexOf(lines[i], at + 1) !== -1) return null
    for (let j = 0; j < lines[i].length; j++) mapped.push(start + at + j)
    if (i < lines.length - 1) mapped.push(offsets[line + 1] - 1)
  }
  return mapped
}

function codeContext (source) {
  const projection = projectComments(source, scanCritic(source).map(span => Object.assign({}, span, { type: 'comment' })))
  const projected = projection.source
  const original = offset => projection.originalOffset(offset)
  const env = {}
  const tokens = md.parse(projected, env)
  const offsets = lineOffsets(projected)
  const spans = []
  const ambiguous = []
  for (const token of tokens) {
    if (token.type !== 'inline' || !token.map) continue
    const mapped = inlineOffsets(projected, offsets, token)
    for (const child of token.children || []) {
      if (child.type !== 'code_inline' || !child.meta) continue
      const { from, to } = child.meta
      if (!mapped || mapped[from] === undefined || mapped[to - 1] === undefined) {
        ambiguous.push([original(offsets[token.map[0]]), original(offsets[token.map[1]] || projected.length)])
        continue
      }
      const start = original(mapped[from])
      const end = original(mapped[to - 1]) + 1
      if (source.slice(start, end) !== token.content.slice(from, to)) {
        ambiguous.push([start, end])
        continue
      }
      spans.push({ from: start, to: end, delimiter: child.markup, contentFrom: start + child.markup.length, contentTo: end - child.markup.length })
    }
    for (const unmatched of env.suggestionUnmatched || []) {
      if (unmatched.content !== token.content || !mapped || mapped[unmatched.from] === undefined) continue
      ambiguous.push([original(mapped[unmatched.from]), original(offsets[token.map[1]] || projected.length)])
    }
  }
  return { spans, ambiguous, literals: literalRanges(projected).map(([from, to]) => [original(from), original(to)]) }
}

function intersects (from, to, start, end) {
  return from === to ? from > start && from < end : from < end && to > start
}

function codeBranch (content, delimiter) {
  if (!content) return { text: '', contentOffset: 0 }
  const runs = content.match(/`+/g) || []
  if (runs.some(run => run.length === delimiter.length)) {
    delimiter = '`'.repeat(runs.reduce((length, run) => Math.max(length, run.length), 0) + 1)
  }
  const padding = content[0] === '`' || content[content.length - 1] === '`' ? ' ' : ''
  return { text: delimiter + padding + content + padding + delimiter, contentOffset: delimiter.length + padding.length }
}

function result (source, from, to, text, start, length) {
  const candidate = source.slice(0, from) + text + source.slice(to)
  if (!scanCritic(candidate).some(span => ['sub', 'ins', 'del'].includes(span.type) && span.from === from && span.raw === text)) {
    return { error: unsafeMessage }
  }
  return { from, to, text, selectionFrom: from + start, selectionTo: from + start + length }
}

/** Plan one editor mutation; all offsets refer to the original UTF-16 source. */
function planCriticSuggestion (source, from, to, type, insertedText) {
  const refuse = () => ({ error: unsafeMessage })
  if (typeof source !== 'string' || !Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < from || to > source.length || !['sub', 'ins', 'del'].includes(type)) return refuse()
  if (insertedText !== undefined && (type !== 'ins' || from !== to || typeof insertedText !== 'string')) return refuse()
  const selection = source.slice(from, to)
  if (unsafeCritic.test(selection) || (insertedText !== undefined && unsafeCritic.test(insertedText))) return refuse()
  const context = codeContext(source)
  const touched = context.spans.filter(span => intersects(from, to, span.from, span.to))
  const containing = touched.find(span => (from >= span.contentFrom && to <= span.contentTo) || (from === span.from && to === span.to))
  if (context.literals.some(([start, end]) => intersects(from, to, start, end))) return refuse()
  if (scanCritic(source).some(span => intersects(from, to, span.from, span.to))) return refuse()
  if (!containing) {
    if (touched.some(span => from > span.from || to < span.to) || context.ambiguous.some(([start, end]) => intersects(from, to, start, end))) return refuse()
    const content = insertedText === undefined ? selection : insertedText
    if (type === 'sub') return result(source, from, to, '{~~' + content + '~>' + content + '~~}', 5 + content.length, content.length)
    const marker = type === 'ins' ? '++' : '--'
    return result(source, from, to, '{' + marker + content + marker + '}', 3, content.length)
  }
  const span = containing
  const original = source.slice(span.from, span.to)
  if (unsafeCritic.test(original)) return refuse()
  const content = source.slice(span.contentFrom, span.contentTo)
  const start = Math.max(0, from - span.contentFrom)
  const end = Math.min(content.length, to - span.contentFrom)
  const removed = content.slice(0, start) + content.slice(end)
  const originalBranch = { text: original, contentOffset: span.delimiter.length }
  const oldBranch = type === 'ins' && insertedText === undefined ? codeBranch(removed, span.delimiter) : originalBranch
  const newBranch = type === 'del'
    ? codeBranch(removed, span.delimiter)
    : insertedText !== undefined
      ? codeBranch(content.slice(0, start) + insertedText + content.slice(end), span.delimiter)
      : originalBranch
  for (const branch of [oldBranch, newBranch]) {
    if (!branch.text) continue
    if (/\n[ \t]*\n/.test(branch.text)) return refuse()
    const candidate = source.slice(0, span.from) + branch.text + source.slice(span.to)
    if (!codeContext(candidate).spans.some(found => found.from === span.from && found.to === span.from + branch.text.length)) return refuse()
  }
  const length = type === 'del' ? 0 : insertedText === undefined ? end - start : insertedText.length
  const offset = newBranch.text ? newBranch.contentOffset + start : 0
  return result(source, span.from, span.to, '{~~' + oldBranch.text + '~>' + newBranch.text + '~~}', 5 + oldBranch.text.length + offset, length)
}

module.exports = { planCriticSuggestion }
